import type { AppContext } from "next/app";
import React from "react";

import {
  THE_VERGE_COMMUNITY_ID,
  THE_VERGE_CYCLONE_COMMUNITY_ID,
} from "@voxmedia/duet-framework/config/community/communities";
import ThemeProvider from "@voxmedia/duet-framework/components/app/ThemeProvider";
import DuetApp from "@voxmedia/duet-framework/pages/_app";
import VergeCSKAppComponent from "src/verge-csk-app";
import overtone from "@voxmedia/duet-ui/themes/overtone.css";
import vergeDark from "@voxmedia/duet-ui/themes/theverge-dark.css";
import vergeLight from "@voxmedia/duet-ui/themes/theverge-light.css";
import "@voxmedia/duet-ui/shared-styles/coral-token-connect.css";
import "src/styles/coral-theme.css";
import "@styles/global.css";

import { vergeGetDrawerComponents } from "src/config/verge-drawer-components";
import CskAppContentWrapper from "src/components/dev/CommentsPreview/CskAppContentWrapper";
import { CskPageOptions } from "src/utils/create-get-static-props";

function VergeCSKApp(...args: Parameters<typeof VergeCSKAppComponent>) {
  return VergeCSKAppComponent(...args);
}

/**
 * In development, wrap the app in a node with suppressHydrationWarning so
 * third-party scripts (e.g. Concert ads) that mutate the DOM after load do not
 * trigger React hydration errors and obscure real issues.
 */
function App(
  props: React.ComponentProps<typeof VergeCSKApp>,
): React.ReactElement {
  const { pageProps = {}, themes, defaultTheme, ...rest } = props;
  const cskPageProps = pageProps as typeof pageProps & CskPageOptions;
  const mergedPageProps = cskPageProps.drawerThemeSwitcher
    ? {
        ...cskPageProps,
        navProps: {
          getDrawerComponents: vergeGetDrawerComponents,
          ...cskPageProps.navProps,
        },
      }
    : cskPageProps;

  const content = (
    <ThemeProvider
      themes={themes}
      defaultTheme={defaultTheme}
      storageKey="verge-theme"
      systemThemeMap={{ dark: "vergeDark", light: "vergeLight" }}
      suggestedTheme={
        cskPageProps.drawerThemeSwitcher ? undefined : cskPageProps.pageTheme
      }
    >
      <VergeCSKApp
        {...rest}
        AppContentWrapper={CskAppContentWrapper}
        themes={themes}
        defaultTheme={defaultTheme}
        pageProps={mergedPageProps}
      />
    </ThemeProvider>
  );
  if (process.env.NODE_ENV === "development") {
    return <div suppressHydrationWarning>{content}</div>;
  }
  return content;
}

const themes =
  process.env.NODE_ENV === "development"
    ? {
        overtone,
        vergeLight,
        vergeDark,
        light: vergeLight,
        dark: vergeDark,
      }
    : {
        overtone,
        vergeLight,
        vergeDark,
      };

// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
App.getInitialProps = async (context: AppContext) => {
  if (context.ctx.req) {
    /*
     * We set a default host for the build step because Next supplies a
     * synthetic request with an empty headers object. Some of the
     * functionality in SSRQueryFetcher relies on having a host URL.
     * (Specifically, `bypassWpCacheEnabled` as of writing.)
     */
    const port = process.env.PORT ?? "3005";
    context.ctx.req.headers["host"] = `http://localhost:${port}`;
    /**
     * In DuetApp.getInitialProps, we use the `vm-cyclone-community-id` header to
     * determine the cyclone community ID which is passed to the App layout in
     * order to provide the value to the community provider. Since there is no
     * middleware to set this header, we set it here.
     */
    context.ctx.req.headers["vm-cyclone-community-id"] =
      THE_VERGE_CYCLONE_COMMUNITY_ID;
  }
  // No server auth state because there's no server.
  const asPath = context.ctx.asPath ?? context.router?.asPath ?? "";
  const omitThirdPartyTrackers = asPath.includes("tip-the-verge");
  const props = await DuetApp.getInitialProps({
    context,
    themes,
    defaultTheme: "vergeLight",
    themeStorageKey: "verge-theme",
    systemThemeMap: { dark: "vergeDark", light: "vergeLight" },
    communityId: THE_VERGE_COMMUNITY_ID,
    scripts: omitThirdPartyTrackers
      ? []
      : [
          {
            src: "https://cdn.bullwhip.cloud/sonar/vox-verge.umd.js",
            strategy: "beforeInteractive",
            async: true,
          },
        ],
  });
  // CSK stories don't receive vm-resource-is-paywall-eligible from middleware; force true so gift link etc. can show.
  // `preview` skips GTM in DuetApp; use it for the tracker-free tips page.
  return {
    ...props,
    isPaywallEligible: true,
    ...(omitThirdPartyTrackers ? { preview: true } : {}),
  };
};

export default App;
